import {
    Component,
    ComponentRef,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    Output,
    SimpleChanges,
    ViewChild,
    ViewContainerRef,
} from '@angular/core';
import { VersionChangesPanelService } from '@service/version-changes-panel.service';
import { VersionsHistoryService } from '@service/versions-history.service';
import { VersionChangeType } from 'icreate/common/models/version-change-panel';
import { FOLDER_CREATED, PublishedVersion, VersionChanges } from 'icreate/common/models/versions-history';
import { ProjectStatus, Vessel } from 'icreate/common/models/vessel';

@Component({
    selector: 'app-version-changes',
    templateUrl: './version-changes.component.html',
    styleUrls: ['./version-changes.component.css'],
    standalone: false,
})
export class VersionChangesComponent implements OnChanges, OnDestroy {
    @Input() vessel: Vessel;
    @Input() version: PublishedVersion;
    @Input() previousVersion: PublishedVersion;
    @Input() firstVersion: PublishedVersion;
    @Input() iApiShipyardId: string;
    @Output() onRestoreVersion: EventEmitter<PublishedVersion> = new EventEmitter();
    @ViewChild('changesContainer', { read: ViewContainerRef, static: true })
    changesContainer: ViewContainerRef;

    isLoadingChanges: boolean = false;

    versionChanges: VersionChanges;

    panelComponentArray: ComponentRef<any>[] = [];

    constructor(
        private versionsHistoryService: VersionsHistoryService,
        private panelFactory: VersionChangesPanelService
    ) {}

    async ngOnChanges(changes: SimpleChanges): Promise<void> {
        if (!this.iApiShipyardId || !this.vessel) {
            return;
        }
        if (changes.version) {
            this.isLoadingChanges = true;
            try {
                this.destroyPanels();
                if (!this.version) {
                    this.isLoadingChanges = false;
                    return;
                }
                if (this.version.name == FOLDER_CREATED) {
                    let initialValues;
                    if (this.firstVersion && this.firstVersion.publishedDate) {
                        initialValues = await this.versionsHistoryService.getVersionDifferences(
                            this.iApiShipyardId,
                            this.vessel.iAPIVesselId,
                            this.firstVersion.name,
                            undefined
                        );
                    }
                    this.createdFolderCreatedPanels(initialValues);
                    this.isLoadingChanges = false;
                    return;
                }
                if (!this.version.publishedDate) {
                    this.versionChanges = await this.versionsHistoryService.compareCurrent(
                        this.iApiShipyardId,
                        this.vessel.id,
                        this.vessel.iAPIVesselId
                    );
                } else {
                    this.versionChanges = await this.versionsHistoryService.getVersionDifferences(
                        this.iApiShipyardId,
                        this.vessel.iAPIVesselId,
                        this.version.name,
                        this.previousVersion?.name
                    );
                }

                this.createChangesPanels();
            } catch (err) {
                console.error(err);
            }
            this.isLoadingChanges = false;
        }
    }

    ngOnDestroy(): void {
        this.destroyPanels();
    }

    createChangesPanels() {
        if (!this.versionChanges) {
            return;
        }
        const versionChangesKeys = Object.keys(this.versionChanges);

        const orderedPanels = Object.values(VersionChangeType) as any;

        const orderedPanelsArray: string[] = versionChangesKeys
            .filter(color => orderedPanels.includes(color))
            .sort((a, b) => orderedPanels.indexOf(a) - orderedPanels.indexOf(b));

        for (let i = 0; i < orderedPanelsArray.length; i++) {
            let changePanel: any;
            try {
                changePanel = this.panelFactory.getPanel(orderedPanelsArray[i]);
            } catch (err) {
                return;
            }

            if (orderedPanelsArray[i] === VersionChangeType.APPROVER) {
                if (
                    this.versionChanges[orderedPanelsArray[i]].approved?.length === 0 &&
                    this.versionChanges[orderedPanelsArray[i]].rejected?.length === 0
                ) {
                    continue;
                }
            }

            if (!this.versionChanges[orderedPanelsArray[i]]) continue;

            let panelComponent = this.changesContainer.createComponent(changePanel);
            this.panelComponentArray.push(panelComponent);

            this.changesContainer.element.nativeElement.appendChild(panelComponent.location.nativeElement);

            if (orderedPanelsArray[i] === VersionChangeType.Owner) {
                (panelComponent.instance as any).version = this.version.name;
                (panelComponent.instance as any).owner = this.versionChanges[orderedPanelsArray[i]];
                (panelComponent.instance as any).editors = this.versionChanges['editors'];
            } else {
                (panelComponent.instance as any).changes = this.versionChanges[orderedPanelsArray[i]];
                (panelComponent.instance as any).version = this.version.name;
            }
        }
    }

    destroyPanels() {
        this.panelComponentArray.forEach(panelComponent => {
            panelComponent.destroy();
            panelComponent.location.nativeElement.remove();
        });
    }

    projectIsActive(): boolean {
        return this.vessel.projectStatus != ProjectStatus.undefined;
    }

    isCreationFolder(): boolean {
        return this.version.name == FOLDER_CREATED;
    }

    restoreVersion(version) {
        this.onRestoreVersion.emit(version);
    }

    createdFolderCreatedPanels(initialValues: VersionChanges) {
        const createdPanel = this.panelFactory.getPanel(VersionChangeType.CREATED);
        const createdPanelComponent = this.changesContainer.createComponent(createdPanel);
        this.panelComponentArray.push(createdPanelComponent);
        this.changesContainer.element.nativeElement.appendChild(createdPanelComponent.location.nativeElement);
        (createdPanelComponent.instance as any).vessel = this.vessel;

        const opsPanel = this.panelFactory.getPanel(VersionChangeType.OPS);
        const opsPanelComponent = this.changesContainer.createComponent(opsPanel);
        this.panelComponentArray.push(opsPanelComponent);
        this.changesContainer.element.nativeElement.appendChild(opsPanelComponent.location.nativeElement);
        const intialOps = initialValues?.numberOps ? initialValues.numberOps.old : this.vessel.numberOps;
        (opsPanelComponent.instance as any).numberOPS = intialOps;

        const fcuPanel = this.panelFactory.getPanel(VersionChangeType.FCU);
        const fcuPanelComponent = this.changesContainer.createComponent(fcuPanel);
        this.panelComponentArray.push(fcuPanelComponent);
        this.changesContainer.element.nativeElement.appendChild(fcuPanelComponent.location.nativeElement);
        const initialFcu = initialValues?.numberFcu ? initialValues.numberFcu.old : this.vessel.numberFcu;
        (fcuPanelComponent.instance as any).numberFcu = initialFcu;
    }
}
